import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MissingPositionDTO, SavePositionErrorDTO } from 'api/model';
import {
  useRoutePlanningSettingsGetMissingPositions,
  useRoutePlanningSettingsSavePositions,
  useRoutePlanningSettingsSuggestPositions,
} from 'api/route-planning-settings/route-planning-settings';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Checkbox from 'components/inputs/Checkbox';
import Input from 'components/inputs/Input';
import TextButton from 'components/inputs/TextButton';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { showException } from 'utils/exception-helper';
import { parseCoordinates } from './coordinates';

/** Samma tak som backend (AddressPositionLogic.MaxSuggestBatch): varje adress är ett geokodningsanrop. */
const MAX_SUGGEST_BATCH = 100;

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 1300px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const Note = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
`;

const Badge = styled.span`
  display: inline-block;
  margin-right: 4px;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 11px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const Muted = styled.span`
  color: #999;
`;

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.foreground.error};
  font-size: 12px;
`;

type RowState = {
  selected: boolean;
  /** Det som står i positionsfältet — ett geokodat förslag eller inklistrat för hand. */
  text: string;
  /** Satt efter ett förslagsanrop: hittade geokodaren något? */
  suggestionFound?: boolean;
  query?: string;
  error?: string;
};

const EMPTY: RowState = { selected: false, text: '' };

const isPlanningPlace = (r: MissingPositionDTO) =>
  r.isZakraHub ||
  r.isLogisticsCenter ||
  r.isCentralStation ||
  r.isDriverSwapPoint ||
  r.homeAnchorUserCount > 0;

const kindBadges = (r: MissingPositionDTO) => (
  <>
    {r.isZakraHub && <Badge>Hub</Badge>}
    {r.isLogisticsCenter && <Badge>Logistikcenter</Badge>}
    {r.isCentralStation && <Badge>Centralstation</Badge>}
    {r.isDriverSwapPoint && <Badge>Bytespunkt</Badge>}
    {r.isRiddermark && <Badge>Riddermark</Badge>}
  </>
);

const mapsLink = (text: string) => {
  const point = parseCoordinates(text);
  if (!point) return null;
  return (
    <a
      href={`https://www.google.com/maps?q=${point.latitude},${point.longitude}`}
      rel="noreferrer"
      target="_blank"
    >
      karta
    </a>
  );
};

/**
 * Inställningar → Ruttplanering → Adresser utan position.
 *
 * Ruttplaneringen räknar i koordinater: ett företag utan position syns inte i hubblogiken, går
 * inte att välja som hemankare och ger inget avstånd. Positionen sattes tidigare en i taget med
 * kartväljaren på företagskortet. Här listas alla aktiva adresser som saknar den, planeringsplatser
 * (hubbar, stationer, bytespunkter, hemankare) först, och kan rättas i klump:
 *
 * 1. markera rader och hämta förslag — geokodaren slår upp adressen men inget sparas,
 * 2. granska förslaget (länken öppnar kartan) eller klistra in en egen koordinat,
 * 3. spara de markerade raderna som har en position.
 */
const MissingPositions = () => {
  const missingCall = useRoutePlanningSettingsGetMissingPositions();
  const suggestCall = useRoutePlanningSettingsSuggestPositions();
  const saveCall = useRoutePlanningSettingsSavePositions();

  const [state, setState] = useState<Record<number, RowState>>({});
  const [onlyPlanningPlaces, setOnlyPlanningPlaces] = useState(false);
  const [search, setSearch] = useState('');
  const [lastSaved, setLastSaved] = useState<number | null>(null);

  const rowState = (id: number) => state[id] ?? EMPTY;
  const patchRow = (id: number, patch: Partial<RowState>) =>
    setState((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? EMPTY), ...patch },
    }));

  const allRows = useMemo(() => missingCall.data ?? [], [missingCall.data]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return allRows.filter(
      (r) =>
        (!onlyPlanningPlaces || isPlanningPlace(r)) &&
        (needle.length === 0 ||
          `${r.name} ${r.address} ${r.zip} ${r.city}`
            .toLowerCase()
            .includes(needle))
    );
  }, [allRows, onlyPlanningPlaces, search]);

  const selectedIds = rows
    .filter((r) => rowState(r.companyID).selected)
    .map((r) => r.companyID);
  const savable = selectedIds
    .map((id) => ({ id, point: parseCoordinates(rowState(id).text) }))
    .filter((x) => x.point !== null);
  const allVisibleSelected =
    rows.length > 0 && selectedIds.length === rows.length;
  const planningPlaceCount = allRows.filter(isPlanningPlace).length;

  const toggleAllVisible = () => {
    const select = !allVisibleSelected;
    setState((prev) => {
      const next = { ...prev };
      rows.forEach((r) => {
        next[r.companyID] = {
          ...(next[r.companyID] ?? EMPTY),
          selected: select,
        };
      });
      return next;
    });
  };

  const suggest = async () => {
    const ids = selectedIds.slice(0, MAX_SUGGEST_BATCH);
    try {
      const suggestions = await suggestCall.mutateAsync({
        data: { companyIDs: ids },
      });
      setState((prev) => {
        const next = { ...prev };
        suggestions.forEach((s) => {
          const current = next[s.companyID] ?? EMPTY;
          next[s.companyID] = {
            ...current,
            suggestionFound: s.found,
            query: s.query,
            error: undefined,
            // Skriv inte över en koordinat som någon redan klistrat in för hand.
            text:
              s.found && current.text.trim().length === 0
                ? `${s.latitude}, ${s.longitude}`
                : current.text,
          };
        });
        return next;
      });
    } catch (error) {
      showException(error);
    }
  };

  const save = async () => {
    try {
      const result = await saveCall.mutateAsync({
        data: {
          positions: savable.map(({ id, point }) => ({
            companyID: id,
            latitude: point!.latitude,
            longitude: point!.longitude,
          })),
        },
      });
      const errorsById = new Map<number, SavePositionErrorDTO>(
        result.errors.map((e) => [e.companyID, e])
      );
      setState((prev) => {
        const next = { ...prev };
        savable.forEach(({ id }) => {
          const error = errorsById.get(id);
          if (error) {
            next[id] = { ...(next[id] ?? EMPTY), error: error.message };
          } else {
            delete next[id];
          }
        });
        return next;
      });
      setLastSaved(result.savedCount);
      await missingCall.refetch();
    } catch (error) {
      showException(error);
    }
  };

  const columnSettings = useMemo(
    (): ColumnSetting<MissingPositionDTO, object>[] => [
      {
        head: (
          <Checkbox
            checked={allVisibleSelected}
            onChange={toggleAllVisible}
            title="Markera alla synliga"
          />
        ),
        width: 40,
        render: (r) => (
          <Checkbox
            checked={rowState(r.companyID).selected}
            onChange={() =>
              patchRow(r.companyID, {
                selected: !rowState(r.companyID).selected,
              })
            }
          />
        ),
      },
      {
        head: 'Namn',
        width: 260,
        sortFunction: (a, b) => a.name.localeCompare(b.name, 'sv'),
        render: (r) => (
          <>
            {kindBadges(r)}
            {r.name}
          </>
        ),
      },
      {
        head: 'Adress',
        width: 280,
        render: (r) => `${r.address}, ${r.zip} ${r.city}`,
      },
      {
        head: 'Hemankare för',
        width: 110,
        sortFunction: (a, b) => a.homeAnchorUserCount - b.homeAnchorUserCount,
        render: (r) =>
          r.homeAnchorUserCount > 0 ? (
            `${r.homeAnchorUserCount} FT`
          ) : (
            <Muted>–</Muted>
          ),
      },
      {
        head: 'Ärenden 90 d',
        width: 100,
        sortFunction: (a, b) => a.recentCaseCount - b.recentCaseCount,
        render: (r) => r.recentCaseCount,
      },
      {
        head: 'Position (lat, long)',
        width: 330,
        render: (r) => {
          const s = rowState(r.companyID);
          const parsed = parseCoordinates(s.text);
          return (
            <Row>
              <Input
                error={s.text.trim().length > 0 && !parsed}
                onChange={(eve) =>
                  patchRow(r.companyID, {
                    text: eve.target.value,
                    // Den som skriver en position vill spara den raden.
                    selected: true,
                    error: undefined,
                  })
                }
                placeholder="59.3293, 18.0686"
                small
                value={s.text}
                width={180}
              />
              {mapsLink(s.text)}
              {s.suggestionFound === false && s.text.length === 0 && (
                <ErrorText title={s.query}>ingen träff</ErrorText>
              )}
              {s.error && <ErrorText>{s.error}</ErrorText>}
            </Row>
          );
        },
      },
    ],
    // rowState/patchRow läser state; tabellen ska ritas om när den ändras.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, allVisibleSelected, rows]
  );

  const isBusy =
    missingCall.isFetching || suggestCall.isLoading || saveCall.isLoading;

  return (
    <Wrapper>
      <h1>Adresser utan position</h1>
      <Note>
        Aktiva företag som saknar koordinat. Ruttplaneringen mäter allt i
        koordinater, så en hub utan position syns inte i hubblogiken (utfyllnad,
        mellanlandning vid hub), och ett ankare utan position går inte att
        välja. Planeringsplatserna (hubbar, logistikcenter, centralstationer,
        bytespunkter och hemankare) står först.
      </Note>
      <Note>
        Markera rader och hämta förslag — adressen geokodas men ingenting
        sparas. Granska förslaget via kartlänken, eller klistra in en egen
        koordinat, och spara sedan. Högst {MAX_SUGGEST_BATCH} förslag åt gången.
      </Note>

      <Row>
        <span>
          <strong>{allRows.length}</strong> adresser saknar position, varav{' '}
          <strong>{planningPlaceCount}</strong> planeringsplatser.
        </span>
        {isBusy && <FontAwesomeIcon icon={faSpinner} spin />}
        {lastSaved !== null && !isBusy && <span>Sparade {lastSaved}.</span>}
      </Row>

      <Row>
        <Checkbox
          checked={onlyPlanningPlaces}
          onChange={() => setOnlyPlanningPlaces((v) => !v)}
        >
          Bara planeringsplatser
        </Checkbox>
        <Input
          onChange={(eve) => setSearch(eve.target.value)}
          placeholder="Sök namn eller adress"
          value={search}
          width={240}
        />
        <TextButton
          disabled={selectedIds.length === 0 || isBusy}
          onClick={suggest}
        >
          Hämta förslag för markerade (
          {Math.min(selectedIds.length, MAX_SUGGEST_BATCH)})
        </TextButton>
        <TextButton disabled={savable.length === 0 || isBusy} onClick={save}>
          Spara markerade med position ({savable.length})
        </TextButton>
      </Row>

      {missingCall.isError ? (
        <ErrorText>Listan gick inte att hämta.</ErrorText>
      ) : (
        <Table fluid columnSettings={columnSettings} rows={rows} />
      )}
    </Wrapper>
  );
};

export default MissingPositions;
